import React, { useState, useEffect } from "react";
import { lumi } from "../lib/lumi";
import {Hotel, Calendar, DollarSign, Lock, User, CheckCircle, AlertCircle, Edit2, Trash2, Download} from 'lucide-react';
import toast from "react-hot-toast";

// Room data
const ROOM_DATA = [
{ roomType: "Executive Suite", totalRooms: 4, bedType: "1 KB", ratePerNight: 2000 },
{ roomType: "Junior Deluxe Room", totalRooms: 8, bedType: "1 QB", ratePerNight: 1000 },
{ roomType: "Superior Deluxe Room", totalRooms: 8, bedType: "2 QBs", ratePerNight: 1200 },
{ roomType: "Family Unit", totalRooms: 6, bedType: "1 QB, 2 TBs", ratePerNight: 2200 }];


function HotelReservation() {
  const [user, setUser] = useState(lumi.auth.user);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roomAvailability, setRoomAvailability] = useState<any[]>([]);
  const [myReservations, setMyReservations] = useState<any[]>([]);
  const [allReservations, setAllReservations] = useState<any[]>([]);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [editingReservation, setEditingReservation] = useState<any>(null);
  const [editFormData, setEditFormData] = useState<any>(null);
  const [adminFormData, setAdminFormData] = useState({
    memberEmail: "",
    guestFirstName: "",
    guestLastName: "",
    guestPhone: "",
    guestEmail: "",
    roomType: "",
    checkInDate: "",
    checkOutDate: "",
    momoTransactionId: "",
    isRegisteredMember: false
  });

  const [formData, setFormData] = useState({
    guestFirstName: "",
    guestLastName: "",
    guestPhone: "",
    guestEmail: "",
    roomType: "",
    checkInDate: "",
    checkOutDate: "",
    momoTransactionId: ""
  });

  const [calculatedData, setCalculatedData] = useState({
    numberOfNights: 0,
    totalAmount: 0,
    depositAmount: 0,
    outstandingAmount: 0,
    ratePerNight: 0
  });

  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange(({ user }) => {
      setUser(user);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (user) {
      loadRoomAvailability();
      loadMyReservations();
      if (user.userRole === "ADMIN") {
        loadAllReservations();
      }
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    calculateTotal();
  }, [formData.roomType, formData.checkInDate, formData.checkOutDate]);

  const loadRoomAvailability = async () => {
    try {
      const result = await lumi.entities.hotelRooms.list({});
      if (result.list.length === 0) {
        // Initialize room data if empty - Executive Suite and Family Unit fully booked
        const initialized = await Promise.all(
          ROOM_DATA.map((room) =>
          lumi.entities.hotelRooms.create({
            ...room,
            availableRooms: room.roomType === "Executive Suite" || room.roomType === "Family Unit" ? 0 : room.totalRooms
          })
          )
        );
        setRoomAvailability(initialized);
      } else {
        // Update rates if they don't match ROOM_DATA
        const updates = result.list.map(async (room) => {
          const correctData = ROOM_DATA.find((r) => r.roomType === room.roomType);
          if (correctData && room.ratePerNight !== correctData.ratePerNight) {
            return await lumi.entities.hotelRooms.update(room._id, {
              ratePerNight: correctData.ratePerNight
            });
          }
          return room;
        });
        const updated = await Promise.all(updates);
        setRoomAvailability(updated);
      }
    } catch (error) {
      console.error("Failed to load room availability:", error);
    }
  };

  const loadMyReservations = async () => {
    if (!user) return;
    try {
      const result = await lumi.entities.reservations.list({
        filter: { memberId: user.userId },
        sort: { createdAt: -1 }
      });
      setMyReservations(result.list);
    } catch (error) {
      console.error("Failed to load reservations:", error);
    }
  };

  const loadAllReservations = async () => {
    try {
      const result = await lumi.entities.reservations.list({
        sort: { createdAt: -1 }
      });
      setAllReservations(result.list);
    } catch (error) {
      console.error("Failed to load all reservations:", error);
    }
  };

  const calculateTotal = () => {
    if (!formData.roomType || !formData.checkInDate || !formData.checkOutDate) {
      setCalculatedData({
        numberOfNights: 0,
        totalAmount: 0,
        depositAmount: 0,
        outstandingAmount: 0,
        ratePerNight: 0
      });
      return;
    }

    const checkIn = new Date(formData.checkInDate);
    const checkOut = new Date(formData.checkOutDate);
    const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));

    if (nights <= 0) {
      setCalculatedData({
        numberOfNights: 0,
        totalAmount: 0,
        depositAmount: 0,
        outstandingAmount: 0,
        ratePerNight: 0
      });
      return;
    }

    const room = roomAvailability.find((r) => r.roomType === formData.roomType);
    if (!room) return;

    const total = room.ratePerNight * nights;
    const deposit = Math.ceil(total * 0.5);
    const outstanding = total - deposit;

    setCalculatedData({
      numberOfNights: nights,
      totalAmount: total,
      depositAmount: deposit,
      outstandingAmount: outstanding,
      ratePerNight: room.ratePerNight
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAdminInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAdminFormData({ ...adminFormData, [name]: value });
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditFormData({ ...editFormData, [name]: value });
  };

  const handleEditReservation = (reservation: any) => {
    setEditingReservation(reservation);
    setEditFormData({
      guestFirstName: reservation.guestFirstName,
      guestLastName: reservation.guestLastName,
      guestPhone: reservation.guestPhone,
      guestEmail: reservation.guestEmail,
      roomType: reservation.roomType,
      checkInDate: new Date(reservation.checkInDate).toISOString().split('T')[0],
      checkOutDate: new Date(reservation.checkOutDate).toISOString().split('T')[0],
      momoTransactionId: reservation.momoTransactionId,
      status: reservation.status
    });
  };

  const handleSaveEdit = async () => {
    if (!user || user.userRole !== "ADMIN" || !editingReservation) return;

    setSaving(true);
    try {
      const checkIn = new Date(editFormData.checkInDate);
      const checkOut = new Date(editFormData.checkOutDate);
      const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));

      const room = roomAvailability.find((r) => r.roomType === editFormData.roomType);
      if (!room) {
        toast.error("Room type not found");
        setSaving(false);
        return;
      }

      const total = room.ratePerNight * nights;
      const deposit = Math.ceil(total * 0.5);
      const outstanding = total - deposit;

      await lumi.entities.reservations.update(editingReservation._id, {
        guestFirstName: editFormData.guestFirstName,
        guestLastName: editFormData.guestLastName,
        guestPhone: editFormData.guestPhone,
        guestEmail: editFormData.guestEmail,
        roomType: editFormData.roomType,
        checkInDate: checkIn,
        checkOutDate: checkOut,
        numberOfNights: nights,
        totalAmount: total,
        depositAmount: deposit,
        outstandingAmount: outstanding,
        momoTransactionId: editFormData.momoTransactionId,
        status: editFormData.status,
        updatedAt: new Date()
      });

      toast.success("Reservation updated successfully!");
      setEditingReservation(null);
      setEditFormData(null);
      loadAllReservations();
      loadRoomAvailability();
    } catch (error) {
      console.error("Failed to update reservation:", error);
      toast.error("Failed to update reservation. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReservation = async (reservationId: string, roomType: string) => {
    if (!user || user.userRole !== "ADMIN") return;

    if (!confirm("Are you sure you want to delete this reservation?")) return;

    try {
      await lumi.entities.reservations.delete(reservationId);

      // Restore room availability
      const room = roomAvailability.find((r) => r.roomType === roomType);
      if (room) {
        await lumi.entities.hotelRooms.update(room._id, {
          availableRooms: room.availableRooms + 1
        });
      }

      toast.success("Reservation deleted successfully!");
      loadAllReservations();
      loadRoomAvailability();
    } catch (error) {
      console.error("Failed to delete reservation:", error);
      toast.error("Failed to delete reservation. Please try again.");
    }
  };

  const handleExportReservations = () => {
    if (!user || user.userRole !== "ADMIN" || allReservations.length === 0) return;

    try {
      // Prepare CSV headers
      const headers = [
        "Reservation ID",
        "Status",
        "Room Type",
        "Check-In Date",
        "Check-Out Date",
        "Number of Nights",
        "Member Name",
        "Member Email",
        "Guest First Name",
        "Guest Last Name",
        "Guest Phone",
        "Guest Email",
        "Total Amount (GHS)",
        "Deposit Paid (GHS)",
        "Outstanding (GHS)",
        "Momo Transaction ID",
        "Payment Deadline",
        "Created At"
      ];

      // Prepare CSV rows
      const rows = allReservations.map((res) => [
        res._id,
        res.status,
        res.roomType,
        new Date(res.checkInDate).toLocaleDateString(),
        new Date(res.checkOutDate).toLocaleDateString(),
        res.numberOfNights,
        res.memberName,
        res.memberEmail,
        res.guestFirstName,
        res.guestLastName,
        res.guestPhone,
        res.guestEmail,
        res.totalAmount,
        res.depositAmount,
        res.outstandingAmount,
        res.momoTransactionId,
        new Date(res.paymentDeadline).toLocaleDateString(),
        new Date(res.createdAt).toLocaleString()
      ]);

      // Convert to CSV string
      const csvContent = [
        headers.join(","),
        ...rows.map(row => 
          row.map(cell => {
            // Escape commas and quotes in cell content
            const cellStr = String(cell || "");
            if (cellStr.includes(",") || cellStr.includes('"') || cellStr.includes("\n")) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          }).join(",")
        )
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().split('T')[0];
      
      link.setAttribute("href", url);
      link.setAttribute("download", `hotel_reservations_${timestamp}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Reservations exported successfully!");
    } catch (error) {
      console.error("Failed to export reservations:", error);
      toast.error("Failed to export reservations. Please try again.");
    }
  };

  const handleAdminReservation = async () => {
    if (!user || user.userRole !== "ADMIN") return;

    // Validation
    if (!adminFormData.guestFirstName.trim()) {
      toast.error("Please enter guest first name");
      return;
    }
    if (!adminFormData.guestLastName.trim()) {
      toast.error("Please enter guest last name");
      return;
    }
    if (!adminFormData.guestPhone.trim()) {
      toast.error("Please enter guest phone number");
      return;
    }
    if (!adminFormData.guestEmail.trim()) {
      toast.error("Please enter guest email address");
      return;
    }
    if (!adminFormData.roomType) {
      toast.error("Please select a room type");
      return;
    }
    if (!adminFormData.checkInDate || !adminFormData.checkOutDate) {
      toast.error("Please select check-in and check-out dates");
      return;
    }
    if (new Date(adminFormData.checkOutDate) <= new Date(adminFormData.checkInDate)) {
      toast.error("Check-out date must be after check-in date");
      return;
    }
    if (!adminFormData.momoTransactionId.trim()) {
      toast.error("Please enter Momo Transaction ID");
      return;
    }

    setSaving(true);
    try {
      // Check if member email provided and lookup member
      let memberInfo = {
        memberId: "non-registered",
        memberName: "Non-Registered Guest",
        memberEmail: ""
      };

      if (adminFormData.memberEmail.trim()) {
        try {
          const memberResult = await lumi.entities.members.list({
            filter: { email: adminFormData.memberEmail.trim() }
          });
          if (memberResult.list.length > 0) {
            const member = memberResult.list[0];
            memberInfo = {
              memberId: member._id,
              memberName: `${member.firstName} ${member.lastName}`,
              memberEmail: member.email
            };
          }
        } catch (error) {
          console.log("Member lookup failed, treating as non-registered");
        }
      }

      const checkIn = new Date(adminFormData.checkInDate);
      const checkOut = new Date(adminFormData.checkOutDate);
      const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));

      const room = roomAvailability.find((r) => r.roomType === adminFormData.roomType);
      if (!room) {
        toast.error("Room type not found");
        setSaving(false);
        return;
      }

      const total = room.ratePerNight * nights;
      const deposit = Math.ceil(total * 0.5);
      const outstanding = total - deposit;

      // Create reservation (admin can override availability)
      await lumi.entities.reservations.create({
        memberId: memberInfo.memberId,
        memberName: memberInfo.memberName,
        memberEmail: memberInfo.memberEmail,
        guestFirstName: adminFormData.guestFirstName,
        guestLastName: adminFormData.guestLastName,
        guestPhone: adminFormData.guestPhone,
        guestEmail: adminFormData.guestEmail,
        roomType: adminFormData.roomType,
        checkInDate: checkIn,
        checkOutDate: checkOut,
        numberOfNights: nights,
        totalAmount: total,
        depositAmount: deposit,
        outstandingAmount: outstanding,
        momoTransactionId: adminFormData.momoTransactionId,
        momoNumber: "0530661997",
        paymentDeadline: new Date("2026-05-31"),
        status: "confirmed",
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Update room availability if not fully booked
      if (room.availableRooms > 0) {
        await lumi.entities.hotelRooms.update(room._id, {
          availableRooms: room.availableRooms - 1
        });
      }

      // Note: Admin email notifications removed - Lumi email service only supports registered users
      console.log('Manual reservation created by admin:', {
        adminUser: user.userName,
        memberStatus: memberInfo.memberId === "non-registered" ? "Non-Registered Guest" : "Registered Member",
        guestName: `${adminFormData.guestFirstName} ${adminFormData.guestLastName}`,
        roomType: adminFormData.roomType,
        checkIn: checkIn.toLocaleDateString(),
        checkOut: checkOut.toLocaleDateString(),
        nights,
        totalAmount: total
      });

      toast.success("Reservation created successfully by admin!");

      // Reset form
      setAdminFormData({
        memberEmail: "",
        guestFirstName: "",
        guestLastName: "",
        guestPhone: "",
        guestEmail: "",
        roomType: "",
        checkInDate: "",
        checkOutDate: "",
        momoTransactionId: "",
        isRegisteredMember: false
      });
      setShowAdminForm(false);

      // Reload data
      loadRoomAvailability();
      loadAllReservations();
    } catch (error) {
      console.error("Failed to create admin reservation:", error);
      toast.error("Failed to create reservation. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSignIn = async () => {
    try {
      await lumi.auth.signIn();
      toast.success("Successfully signed in!");
    } catch (error) {
      console.error("Sign in failed:", error);
      toast.error("Sign in failed. Please try again.");
    }
  };

  const handleSubmitReservation = async () => {
    if (!user) return;

    // Validation
    if (!formData.guestFirstName.trim()) {
      toast.error("Please enter guest first name");
      return;
    }
    if (!formData.guestLastName.trim()) {
      toast.error("Please enter guest last name");
      return;
    }
    if (!formData.guestPhone.trim()) {
      toast.error("Please enter guest phone number");
      return;
    }
    if (!formData.guestEmail.trim()) {
      toast.error("Please enter guest email address");
      return;
    }
    if (!formData.roomType) {
      toast.error("Please select a room type");
      return;
    }
    if (!formData.checkInDate || !formData.checkOutDate) {
      toast.error("Please select check-in and check-out dates");
      return;
    }
    if (new Date(formData.checkOutDate) <= new Date(formData.checkInDate)) {
      toast.error("Check-out date must be after check-in date");
      return;
    }
    if (!formData.momoTransactionId.trim()) {
      toast.error("Please enter your Momo Transaction ID");
      return;
    }

    const room = roomAvailability.find((r) => r.roomType === formData.roomType);
    if (!room || room.availableRooms <= 0) {
      toast.error("No rooms available for this type");
      return;
    }

    setSaving(true);
    try {
      // Create reservation
      await lumi.entities.reservations.create({
        memberId: user.userId,
        memberName: user.userName,
        memberEmail: user.email,
        guestFirstName: formData.guestFirstName,
        guestLastName: formData.guestLastName,
        guestPhone: formData.guestPhone,
        guestEmail: formData.guestEmail,
        roomType: formData.roomType,
        checkInDate: new Date(formData.checkInDate),
        checkOutDate: new Date(formData.checkOutDate),
        numberOfNights: calculatedData.numberOfNights,
        totalAmount: calculatedData.totalAmount,
        depositAmount: calculatedData.depositAmount,
        outstandingAmount: calculatedData.outstandingAmount,
        momoTransactionId: formData.momoTransactionId,
        momoNumber: "0530661997",
        paymentDeadline: new Date("2026-05-31"),
        status: "confirmed",
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Update room availability
      await lumi.entities.hotelRooms.update(room._id, {
        availableRooms: room.availableRooms - 1
      });

      // Send confirmation email to guest
      try {
        await lumi.tools.email.send({
          to: user.email,
          subject: "Hotel Reservation Confirmation - Royal El Mount",
          fromName: "MOBA91 Hotel Reservations",
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #DC143C;">Reservation Confirmed!</h2>
              <p>Dear ${user.userName},</p>
              <p>Your hotel reservation at <strong>Royal El Mount, Elmina</strong> has been confirmed.</p>
              
              <div style="background-color: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="color: #000; margin-top: 0;">Reservation Details</h3>
                <p><strong>Guest Name:</strong> ${formData.guestFirstName} ${formData.guestLastName}</p>
                <p><strong>Guest Phone:</strong> ${formData.guestPhone}</p>
                <p><strong>Guest Email:</strong> ${formData.guestEmail}</p>
                <p><strong>Room Type:</strong> ${formData.roomType}</p>
                <p><strong>Check-In:</strong> ${new Date(formData.checkInDate).toLocaleDateString()}</p>
                <p><strong>Check-Out:</strong> ${new Date(formData.checkOutDate).toLocaleDateString()}</p>
                <p><strong>Number of Nights:</strong> ${calculatedData.numberOfNights}</p>
              </div>
              
              <div style="background-color: #fff3cd; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="color: #000; margin-top: 0;">Payment Information</h3>
                <p><strong>Total Amount:</strong> ${calculatedData.totalAmount} GHS</p>
                <p><strong>Deposit Paid (50%):</strong> <span style="color: #28a745;">${calculatedData.depositAmount} GHS</span></p>
                <p><strong>Outstanding Balance:</strong> <span style="color: #ffc107;">${calculatedData.outstandingAmount} GHS</span></p>
                <p><strong>Momo Transaction ID:</strong> ${formData.momoTransactionId}</p>
                <p style="color: #dc3545; font-weight: bold;">⚠️ Outstanding balance must be paid by May 2026</p>
              </div>
              
              <p>If you have any questions, please contact us.</p>
              <p style="color: #666; font-size: 12px; margin-top: 30px;">This is an automated confirmation email from MOBA91 Hotel Reservations.</p>
            </div>
          `
        });
      } catch (emailError) {
        console.error("Failed to send confirmation email:", emailError);
        // Don't fail the reservation if email fails
      }

      // Note: Admin email notifications removed - Lumi email service only supports registered users
      // Admin can view all reservations in the admin section below
      console.log('New reservation created:', {
        memberName: user.userName,
        memberEmail: user.email,
        guestName: `${formData.guestFirstName} ${formData.guestLastName}`,
        roomType: formData.roomType,
        checkIn: new Date(formData.checkInDate).toLocaleDateString(),
        checkOut: new Date(formData.checkOutDate).toLocaleDateString(),
        totalAmount: calculatedData.totalAmount
      });

      toast.success("Reservation confirmed! Check your email for details.");

      // Reset form
      setFormData({
        guestFirstName: "",
        guestLastName: "",
        guestPhone: "",
        guestEmail: "",
        roomType: "",
        checkInDate: "",
        checkOutDate: "",
        momoTransactionId: ""
      });

      // Reload data
      loadRoomAvailability();
      loadMyReservations();
      if (user.userRole === "ADMIN") {
        loadAllReservations();
      }
    } catch (error) {
      console.error("Failed to create reservation:", error);
      toast.error("Failed to create reservation. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Lock size={64} className="text-[#DC143C] mx-auto mb-6" />
            <h1 className="text-4xl font-bold mb-4 text-black">Hotel Reservations</h1>
            <p className="text-gray-600 text-lg">Sign in to make a reservation</p>
          </div>

          <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 text-center shadow-lg">
            <h2 className="text-2xl font-bold mb-4 text-black">Members Only</h2>
            <p className="text-gray-600 mb-8">
              Only registered MOBA91 members can make hotel reservations. Sign in to continue.
            </p>

            <button
              onClick={handleSignIn}
              className="px-8 py-4 bg-[#DC143C] hover:bg-red-600 rounded-lg font-semibold transition-all transform hover:scale-105 flex items-center justify-center space-x-2 mx-auto">

              <User size={20} />
              <span className="text-white">Sign In to Continue</span>
            </button>
          </div>
        </div>
      </div>);

  }

  if (loading) {
    return (
      <div className="min-h-screen py-16 bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#DC143C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-br from-[#DC143C] to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Hotel size={40} className="text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-2 text-black">Hotel Reservations</h1>
          <p className="text-gray-600 text-lg">Royal El Mount, Elmina</p>
        </div>

        {/* Room Availability */}
        <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 mb-8 shadow-lg">
          <h2 className="text-2xl font-bold mb-6 text-[#DC143C]">Available Rooms</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roomAvailability.map((room) =>
            <div key={room._id} className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-3">{room.roomType}</h3>
                <div className="space-y-2 text-sm text-gray-300">
                  <div className="flex justify-between">
                    <span>Bed Type:</span>
                    <span className="font-semibold">{room.bedType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Rate:</span>
                    <span className="font-semibold text-[#DC143C]">{room.ratePerNight} GHS/night</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Available:</span>
                    <span className={`font-semibold ${room.availableRooms > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {room.availableRooms} / {room.totalRooms} rooms
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Admin Manual Reservation */}
        {user.userRole === "ADMIN" &&
        <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-8 mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#DC143C]">Admin: Manual Reservation</h2>
              <button
              onClick={() => setShowAdminForm(!showAdminForm)}
              className="px-4 py-2 bg-[#DC143C] hover:bg-red-600 rounded-lg font-semibold transition-all">
                {showAdminForm ? "Hide Form" : "Create Manual Reservation"}
              </button>
            </div>

            {showAdminForm &&
          <div>
                {/* Member Lookup */}
                <div className="bg-[#2A2A2A] border border-[#DC143C]/20 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-bold mb-4 text-white">Member Lookup (Optional)</h3>
                  <p className="text-sm text-gray-400 mb-4">Enter member email to link reservation to registered member. Leave empty for non-registered guests.</p>
                  <input
                type="email"
                name="memberEmail"
                value={adminFormData.memberEmail}
                onChange={handleAdminInputChange}
                placeholder="Member email (optional)"
                className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#DC143C]" />

                </div>

                {/* Guest Information */}
                <div className="bg-[#2A2A2A] border border-[#DC143C]/20 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-bold mb-4 text-white">Guest Information</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Guest First Name <span className="text-[#DC143C]">*</span>
                      </label>
                      <input
                    type="text"
                    name="guestFirstName"
                    value={adminFormData.guestFirstName}
                    onChange={handleAdminInputChange}
                    placeholder="Enter first name"
                    className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#DC143C]" />

                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Guest Last Name <span className="text-[#DC143C]">*</span>
                      </label>
                      <input
                    type="text"
                    name="guestLastName"
                    value={adminFormData.guestLastName}
                    onChange={handleAdminInputChange}
                    placeholder="Enter last name"
                    className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#DC143C]" />

                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Guest Phone # <span className="text-[#DC143C]">*</span>
                      </label>
                      <input
                    type="tel"
                    name="guestPhone"
                    value={adminFormData.guestPhone}
                    onChange={handleAdminInputChange}
                    placeholder="Enter phone number"
                    className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#DC143C]" />

                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Guest Email Address <span className="text-[#DC143C]">*</span>
                      </label>
                      <input
                    type="email"
                    name="guestEmail"
                    value={adminFormData.guestEmail}
                    onChange={handleAdminInputChange}
                    placeholder="Enter email address"
                    className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#DC143C]" />

                    </div>
                  </div>
                </div>

                {/* Room and Dates */}
                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Room Type <span className="text-[#DC143C]">*</span>
                    </label>
                    <select
                  name="roomType"
                  value={adminFormData.roomType}
                  onChange={handleAdminInputChange}
                  className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]">
                      <option value="">Select room type</option>
                      {roomAvailability.map((room) =>
                  <option key={room._id} value={room.roomType}>
                          {room.roomType} ({room.ratePerNight} GHS/night)
                        </option>
                  )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Check-In Date <span className="text-[#DC143C]">*</span>
                    </label>
                    <input
                  type="date"
                  name="checkInDate"
                  value={adminFormData.checkInDate}
                  onChange={handleAdminInputChange}
                  className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]" />

                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Check-Out Date <span className="text-[#DC143C]">*</span>
                    </label>
                    <input
                  type="date"
                  name="checkOutDate"
                  value={adminFormData.checkOutDate}
                  onChange={handleAdminInputChange}
                  className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]" />

                  </div>
                </div>

                {/* Payment */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Momo Transaction ID <span className="text-[#DC143C]">*</span>
                  </label>
                  <input
                type="text"
                name="momoTransactionId"
                value={adminFormData.momoTransactionId}
                onChange={handleAdminInputChange}
                placeholder="Enter transaction ID"
                className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#DC143C]" />

                </div>

                <button
              onClick={handleAdminReservation}
              disabled={saving}
              className="w-full px-8 py-4 bg-[#DC143C] hover:bg-red-600 disabled:bg-[#2A2A2A] disabled:text-gray-500 disabled:cursor-not-allowed rounded-lg font-semibold transition-all flex items-center justify-center space-x-2">
                  <CheckCircle size={20} />
                  <span>{saving ? "Creating..." : "Create Reservation"}</span>
                </button>
              </div>
          }
          </div>
        }

        {/* Reservation Form */}
        <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-8 mb-8">
          <h2 className="text-2xl font-bold mb-6 text-[#DC143C]">Make a Reservation</h2>
          
          {/* Guest Information */}
          <div className="bg-[#2A2A2A] border border-[#DC143C]/20 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-bold mb-4 text-white">Guest Information</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Guest First Name <span className="text-[#DC143C]">*</span>
                </label>
                <input
                  type="text"
                  name="guestFirstName"
                  value={formData.guestFirstName}
                  onChange={handleInputChange}
                  placeholder="Enter first name"
                  className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#DC143C]" />

              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Guest Last Name <span className="text-[#DC143C]">*</span>
                </label>
                <input
                  type="text"
                  name="guestLastName"
                  value={formData.guestLastName}
                  onChange={handleInputChange}
                  placeholder="Enter last name"
                  className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#DC143C]" />

              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Guest Phone # <span className="text-[#DC143C]">*</span>
                </label>
                <input
                  type="tel"
                  name="guestPhone"
                  value={formData.guestPhone}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                  className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#DC143C]" />

              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Guest Email Address <span className="text-[#DC143C]">*</span>
                </label>
                <input
                  type="email"
                  name="guestEmail"
                  value={formData.guestEmail}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                  className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#DC143C]" />

              </div>
            </div>
          </div>

          {/* Room and Dates */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Room Type <span className="text-[#DC143C]">*</span>
              </label>
              <select
                name="roomType"
                value={formData.roomType}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]">

                <option value="">Select a room type</option>
                {roomAvailability.map((room) =>
                <option key={room._id} value={room.roomType} disabled={room.availableRooms <= 0}>
                    {room.roomType} ({room.availableRooms} available)
                  </option>
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Check-In Date <span className="text-[#DC143C]">*</span>
              </label>
              <input
                type="date"
                name="checkInDate"
                value={formData.checkInDate}
                onChange={handleInputChange}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]" />

            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Check-Out Date <span className="text-[#DC143C]">*</span>
              </label>
              <input
                type="date"
                name="checkOutDate"
                value={formData.checkOutDate}
                onChange={handleInputChange}
                min={formData.checkInDate || new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]" />

            </div>
          </div>

          {/* Cost Breakdown */}
          {calculatedData.totalAmount > 0 &&
          <div className="bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                <DollarSign size={20} className="text-[#DC143C]" />
                <span>Cost Breakdown</span>
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span>Number of Nights:</span>
                  <span className="font-semibold">{calculatedData.numberOfNights} nights</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Rate per Night:</span>
                  <span className="font-semibold">{calculatedData.ratePerNight} GHS</span>
                </div>
                <div className="border-t border-[#DC143C]/20 pt-3 flex justify-between text-lg font-bold">
                  <span>Total Amount:</span>
                  <span className="text-[#DC143C]">{calculatedData.totalAmount} GHS</span>
                </div>
                <div className="bg-[#1A1A1A] rounded p-4 mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Required Deposit (50%):</span>
                    <span className="font-semibold text-green-400">{calculatedData.depositAmount} GHS</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Outstanding Balance:</span>
                    <span className="font-semibold text-yellow-400">{calculatedData.outstandingAmount} GHS</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-3">
                    Outstanding balance must be paid by May 2026 or reservation will be cancelled.
                  </p>
                </div>
              </div>
            </div>
          }

          {/* Payment Details */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle size={24} className="text-yellow-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-yellow-400 mb-2">Payment Instructions</h3>
                <ol className="text-sm text-gray-300 space-y-2">
                  <li>1. Send 50% deposit via MTN Mobile Money to: <strong className="text-white">0530661997 (nana kwame duodu)</strong></li>
                  <li>2. Enter your Momo Transaction ID below</li>
                  <li>3. Outstanding balance due by <strong className="text-white">May 2026</strong></li>
                </ol>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Momo Transaction ID <span className="text-[#DC143C]">*</span>
            </label>
            <input
              type="text"
              name="momoTransactionId"
              value={formData.momoTransactionId}
              onChange={handleInputChange}
              placeholder="Enter your transaction ID"
              className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#DC143C]" />

            <p className="text-xs text-gray-400 mt-2">
              You must complete the 50% deposit payment and enter the transaction ID to confirm your reservation.
            </p>
          </div>

          <button
            onClick={handleSubmitReservation}
            disabled={saving || calculatedData.totalAmount === 0 || !formData.momoTransactionId.trim()}
            className="w-full px-8 py-4 bg-[#DC143C] hover:bg-red-600 disabled:bg-[#2A2A2A] disabled:text-gray-500 disabled:cursor-not-allowed rounded-lg font-semibold transition-all flex items-center justify-center space-x-2">

            <CheckCircle size={20} />
            <span>{saving ? "Processing..." : "Confirm Reservation"}</span>
          </button>
        </div>

        {/* Admin - All Reservations */}
        {user.userRole === "ADMIN" && allReservations.length > 0 &&
        <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-8 mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#DC143C]">All Reservations (Admin View)</h2>
              <button
                onClick={handleExportReservations}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg font-semibold transition-all flex items-center space-x-2">
                <Download size={20} />
                <span>Export CSV</span>
              </button>
            </div>
            <div className="space-y-4">
              {allReservations.map((reservation) =>
            <div key={reservation._id} className="bg-[#2A2A2A] border border-[#DC143C]/20 rounded-lg p-6">
                  {editingReservation?._id === reservation._id ?
              <div>
                      <h3 className="text-xl font-bold mb-4 text-[#DC143C]">Edit Reservation</h3>
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Guest First Name</label>
                          <input
                      type="text"
                      name="guestFirstName"
                      value={editFormData.guestFirstName}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]" />

                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Guest Last Name</label>
                          <input
                      type="text"
                      name="guestLastName"
                      value={editFormData.guestLastName}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]" />

                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Guest Phone</label>
                          <input
                      type="tel"
                      name="guestPhone"
                      value={editFormData.guestPhone}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]" />

                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Guest Email</label>
                          <input
                      type="email"
                      name="guestEmail"
                      value={editFormData.guestEmail}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]" />

                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Room Type</label>
                          <select
                      name="roomType"
                      value={editFormData.roomType}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]">

                            {roomAvailability.map((room) =>
                      <option key={room._id} value={room.roomType}>{room.roomType}</option>
                      )}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                          <select
                      name="status"
                      value={editFormData.status}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]">

                            <option value="confirmed">Confirmed</option>
                            <option value="pending">Pending</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Check-In Date</label>
                          <input
                      type="date"
                      name="checkInDate"
                      value={editFormData.checkInDate}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]" />

                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Check-Out Date</label>
                          <input
                      type="date"
                      name="checkOutDate"
                      value={editFormData.checkOutDate}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]" />

                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-300 mb-2">Momo Transaction ID</label>
                          <input
                      type="text"
                      name="momoTransactionId"
                      value={editFormData.momoTransactionId}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg text-white focus:outline-none focus:border-[#DC143C]" />

                        </div>
                      </div>
                      <div className="flex space-x-4">
                        <button
                    onClick={handleSaveEdit}
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded-lg font-semibold transition-all">

                          {saving ? "Saving..." : "Save Changes"}
                        </button>
                        <button
                    onClick={() => {setEditingReservation(null);setEditFormData(null);}}
                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg font-semibold transition-all">

                          Cancel
                        </button>
                      </div>
                    </div> :

              <>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold">{reservation.roomType}</h3>
                      <p className="text-sm text-gray-400">
                        {new Date(reservation.checkInDate).toLocaleDateString()} - {new Date(reservation.checkOutDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    reservation.status === "confirmed" ? "bg-green-500/20 text-green-400" :
                    reservation.status === "cancelled" ? "bg-red-500/20 text-red-400" :
                    "bg-blue-500/20 text-blue-400"}`
                    }>
                      {reservation.status.toUpperCase()}
                      </span>
                      <button
                      onClick={() => handleEditReservation(reservation)}
                      className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
                      title="Edit Reservation">

                        <Edit2 size={16} />
                      </button>
                      <button
                      onClick={() => handleDeleteReservation(reservation._id, reservation.roomType)}
                      className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-all"
                      title="Delete Reservation">

                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div className="md:col-span-3 bg-[#1A1A1A] rounded p-4">
                      <h4 className="text-[#DC143C] font-semibold mb-2">Member Information</h4>
                      <p className="text-gray-400">Name: <span className="text-white font-semibold">{reservation.memberName}</span></p>
                      <p className="text-gray-400">Email: <span className="text-white font-semibold">{reservation.memberEmail}</span></p>
                    </div>
                    
                    <div className="md:col-span-3 bg-[#1A1A1A] rounded p-4">
                      <h4 className="text-[#DC143C] font-semibold mb-2">Guest Information</h4>
                      <p className="text-gray-400">First Name: <span className="text-white font-semibold">{reservation.guestFirstName}</span></p>
                      <p className="text-gray-400">Last Name: <span className="text-white font-semibold">{reservation.guestLastName}</span></p>
                      <p className="text-gray-400">Phone: <span className="text-white font-semibold">{reservation.guestPhone}</span></p>
                      <p className="text-gray-400">Email: <span className="text-white font-semibold">{reservation.guestEmail}</span></p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400">Number of Nights:</p>
                      <p className="font-semibold">{reservation.numberOfNights} nights</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Total Amount:</p>
                      <p className="font-semibold text-[#DC143C]">{reservation.totalAmount} GHS</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Deposit Paid:</p>
                      <p className="font-semibold text-green-400">{reservation.depositAmount} GHS</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Outstanding:</p>
                      <p className="font-semibold text-yellow-400">{reservation.outstandingAmount} GHS</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Momo Transaction ID:</p>
                      <p className="font-semibold">{reservation.momoTransactionId}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Payment Deadline:</p>
                      <p className="font-semibold">May 2026</p>
                    </div>
                  </div>
                  </>
              }
                </div>
            )}
            </div>
          </div>
        }

        {/* My Reservations */}
        {myReservations.length > 0 &&
        <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-6 text-[#DC143C]">My Reservations</h2>
            <div className="space-y-4">
              {myReservations.map((reservation) =>
            <div key={reservation._id} className="bg-[#2A2A2A] border border-[#DC143C]/20 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold">{reservation.roomType}</h3>
                      <p className="text-sm text-gray-400">
                        {new Date(reservation.checkInDate).toLocaleDateString()} - {new Date(reservation.checkOutDate).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                reservation.status === "confirmed" ? "bg-green-500/20 text-green-400" :
                reservation.status === "cancelled" ? "bg-red-500/20 text-red-400" :
                "bg-blue-500/20 text-blue-400"}`
                }>
                      {reservation.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400">Number of Nights:</p>
                      <p className="font-semibold">{reservation.numberOfNights} nights</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Total Amount:</p>
                      <p className="font-semibold text-[#DC143C]">{reservation.totalAmount} GHS</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Deposit Paid:</p>
                      <p className="font-semibold text-green-400">{reservation.depositAmount} GHS</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Outstanding:</p>
                      <p className="font-semibold text-yellow-400">{reservation.outstandingAmount} GHS</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Momo Transaction ID:</p>
                      <p className="font-semibold">{reservation.momoTransactionId}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Payment Deadline:</p>
                      <p className="font-semibold">May 2026</p>
                    </div>
                  </div>
                </div>
            )}
            </div>
          </div>
        }
      </div>
    </div>);

}

export default HotelReservation;